"use client";

import type { FC, ReactNode } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import {
  Card,
  type CardProps,
  Image,
  type ImageProps,
  useParallax,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";

import { containerStyle, contentStyle, imageStyle } from "./ImageCard.css";

export type ImageCardProps = Pick<ImageProps, "src" | "alt"> &
  CardProps &
  RecipeVariants<typeof containerStyle> & {
    justifyContent?: "flex-start" | "flex-end" | "center" | "space-between";
    /**
     * Enable elevation on the content card
     */
    contentElevation?: boolean;
    /**
     * Enable parallax effect on the background image
     */
    parallax?: boolean;
    /**
     * Parallax intensity (0-1), defaults to 0.2
     */
    parallaxIntensity?: number;
    /**
     * Children to render inside the image
     */
    imageChildren?: ReactNode;
    /**
     * Position of the image
     */
    imagePosition?: ImageProps["position"];
  };

export const ImageCard: FC<ImageCardProps> = ({
  src,
  alt,
  ratio,
  justifyContent,
  contentElevation,
  parallax = false,
  parallaxIntensity = 0.05,
  imageChildren,
  contentClassName,
  leading,
  children,
  trailing,
  className,
  imagePosition = "left",
  ...props
}) => {
  const parallaxResult = useParallax<HTMLDivElement>({
    enabled: parallax,
    intensity: parallaxIntensity,
    inverted: true,
  });
  const { ref: parallaxRef, style: parallaxStyle } = parallaxResult;

  return (
    <Card
      variant="tertiary"
      overflowHidden
      leading={
        <Image
          ref={parallaxRef}
          className={imageStyle}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            ...(parallax ? parallaxStyle : {}),
          }}
          src={src}
          alt={alt}
          height="100%"
          width="100%"
          position={imagePosition}
        >
          {imageChildren}
        </Image>
      }
      contentClassName={mergeClasses(
        contentStyle({ justifyContent }),
        contentClassName,
      )}
      className={mergeClasses(containerStyle({ ratio }), className)}
      {...props}
    >
      {leading}
      {children && (
        <Card variant="blur" gap="xs" elevated={contentElevation}>
          {children}
        </Card>
      )}
      {trailing}
    </Card>
  );
};
